import {
    AccountCircle,
    FormatQuote,
    Verified
} from '@mui/icons-material';
import {
    alpha,
    Avatar,
    Box,
    Card,
    CardContent,
    Container,
    Grid,
    IconButton,
    Rating,
    Stack,
    Typography,
    useMediaQuery,
    useTheme
} from '@mui/material';
import { motion, Variants } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import ApplicationButton from '../../../../Components/ApplicationButton';
import { getOrDefault } from '../../../../Helpers/Utils';
import { ClientTestimonialsProps, Testimonial, TestimonialCardProps } from './types';

const ClientTestimonials: React.FC<ClientTestimonialsProps> = ({ spec, type, index }) => {
    const theme = useTheme();
    const isDownMedium = useMediaQuery(theme.breakpoints.down('md'));
    const [currentTestimonial, setCurrentTestimonial] = useState<number>(0);

    // Extract spec properties with defaults
    const backgroundColor = getOrDefault(spec.backgroundColor, theme.palette.background.default);
    const titleColor = getOrDefault(spec.titleColor, theme.palette.text.primary);
    const subtitleColor = getOrDefault(spec.subtitleColor, theme.palette.text.secondary);
    const accentColor = getOrDefault(spec.accentColor, theme.palette.primary.main);

    const title = getOrDefault(spec.title, 'ماذا يقول عملاؤنا');
    const subtitle = getOrDefault(spec.subtitle, 'آراء حقيقية من عملاء سافروا معنا واستمتعوا بتجارب لا تُنسى');

    const actionButtonText = getOrDefault(spec.actionButtonText, null)
    const actionButtonTextColor = getOrDefault(spec.actionButtonTextColor, "primary.contrast")
    const actionButtonBackground = getOrDefault(spec.actionButtonBackground, "#ccc")
    const actionButtonHref = getOrDefault(spec.actionButtonHref, "#")
    const actionButtonVariant = getOrDefault(spec.actionButtonVariant, "contained")

    const secondaryButtonText = getOrDefault(spec.secondaryButtonText, null)
    const secondaryButtonTextColor = getOrDefault(spec.secondaryButtonTextColor, "primary.contrast")
    const secondaryButtonBackground = getOrDefault(spec.secondaryButtonBackground, "#ccc")
    const secondaryButtonHref = getOrDefault(spec.secondaryButtonHref, "#")
    const secondaryButtonVariant = getOrDefault(spec.secondaryButtonVariant, "outlined")

    // Default testimonials for tourism agency
    const defaultTestimonials: Testimonial[] = [
        {
            id: 1,
            name: 'أحمد محمد',
            location: 'الرياض، السعودية',
            rating: 5,
            comment: 'تجربة رائعة مع سفرة وحكاية! تنظيم مثالي للرحلة إلى تركيا، وخدمة عملاء ممتازة. أنصح الجميع بالتعامل معهم.',
            tripDestination: null,
            avatar: null,
            verified: true
        },
        {
            id: 2,
            name: 'فاطمة العلي',
            location: 'الكويت',
            rating: 5,
            comment: 'رحلة العمر إلى باريس! كل شيء كان منظم بدقة، الفندق رائع والجولات السياحية كانت مذهلة. شكراً لفريق سفرة وحكاية.',
            tripDestination: null,
            avatar: null,
            verified: true
        },
        {
            id: 3,
            name: 'خالد الشمري',
            location: 'الدوحة، قطر',
            rating: 5,
            comment: 'خدمة احترافية من البداية للنهاية. رحلة شهر العسل إلى جزر المالديف كانت حلم تحقق. أسعار ممتازة وجودة خدمة عالية.',
            tripDestination: null,
            avatar: null,
            verified: true
        },
        {
            id: 4,
            name: 'مريم الزهراني',
            location: 'جدة، السعودية',
            rating: 4,
            comment: 'رحلة عائلية ممتعة إلى دبي. الأطفال استمتعوا كثيراً وكانت البرامج مناسبة للعائلة. تنظيم جيد ومرشدين متعاونين.',
            tripDestination: null,
            avatar: null,
            verified: true
        },
        {
            id: 5,
            name: 'سعد البقمي',
            location: 'مكة، السعودية',
            rating: 5,
            comment: 'أفضل وكالة سفر تعاملت معها! رحلة العمرة كانت مريحة ومنظمة بشكل مثالي. الفريق محترف ويهتم بأدق التفاصيل.',
            tripDestination: null,
            avatar: null,
            verified: true
        },
        {
            id: 6,
            name: 'نورا الحربي',
            location: 'الرياض، السعودية',
            rating: 5,
            comment: 'تجربة سفر استثنائية إلى اليونان! الجزر كانت خيالية والفنادق ممتازة. سفرة وحكاية جعلت حلمي يتحقق.',
            tripDestination: null,
            avatar: null,
            verified: true
        }
    ];

    const testimonials = getOrDefault(spec.testimonials, defaultTestimonials).slice(0, isDownMedium ? 3 : 6);

    const autoRotate = getOrDefault(spec.autoRotate, true);
    const rotationInterval = getOrDefault(spec.rotationInterval, 5000);

    // Auto rotation effect
    useEffect(() => {
        if (autoRotate && testimonials.length > 1) {
            const interval = setInterval(() => {
                setCurrentTestimonial((prev) =>
                    prev === testimonials.length - 1 ? 0 : prev + 1
                );
            }, rotationInterval);

            return () => clearInterval(interval);
        }
        return undefined;
    }, [autoRotate, rotationInterval, testimonials.length]);

    // Animation variants
    const containerVariants: Variants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.1
            }
        }
    };

    const cardVariants: Variants = {
        hidden: {
            opacity: 0,
            y: 50,
            scale: 0.9
        },
        visible: {
            opacity: 1,
            y: 0,
            scale: 1,
            transition: {
                type: "spring" as const,
                stiffness: 100,
                damping: 12
            }
        }
    };

    const TestimonialCard: React.FC<TestimonialCardProps> = ({ testimonial, isActive = false, onClick }) => {
        return (
            <motion.div
                variants={cardVariants}
                whileHover={{
                    scale: 1.02,
                    transition: { type: "spring" as const, stiffness: 300 }
                }}
                whileTap={{ scale: 0.98 }}
                onClick={onClick}
                style={{ cursor: 'pointer' }}
            >
                <Card
                    sx={{
                        height: '100%',
                        borderRadius: 3,
                        boxShadow: theme.palette.mode === 'dark'
                            ? (isActive
                                ? '0 12px 40px rgba(0,0,0,0.7)'
                                : '0 4px 20px rgba(0,0,0,0.5)')
                            : (isActive
                                ? '0 12px 40px rgba(0,0,0,0.15)'
                                : '0 4px 20px rgba(0,0,0,0.08)'),
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        background: theme.palette.mode === 'dark'
                            ? (isActive
                                ? `linear-gradient(135deg, ${alpha(accentColor, 0.15)}, ${alpha(accentColor, 0.05)})`
                                : `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 1)}, ${alpha(theme.palette.background.default, 0.8)})`)
                            : (isActive
                                ? `linear-gradient(135deg, ${accentColor}08, ${accentColor}03)`
                                : 'linear-gradient(135deg, #fff 0%, #f8f9fa 100%)'),
                        border: theme.palette.mode === 'dark'
                            ? (isActive
                                ? `2px solid ${alpha(accentColor, 0.5)}`
                                : `1px solid rgba(255, 255, 255, 0.1)`)
                            : (isActive
                                ? `2px solid ${accentColor}30`
                                : '1px solid rgba(0,0,0,0.04)'),
                        transform: isActive ? 'translateY(-4px)' : 'none',
                        '&:hover': {
                            boxShadow: theme.palette.mode === 'dark'
                                ? '0 12px 40px rgba(0,0,0,0.6)'
                                : '0 12px 40px rgba(0,0,0,0.12)',
                            transform: 'translateY(-4px)',
                        }
                    }}
                >
                    {/* Quote icon background */}
                    <Box
                        sx={{
                            position: 'absolute',
                            top: -10,
                            right: -10,
                            width: 60,
                            height: 60,
                            borderRadius: '50%',
                            background: theme.palette.mode === 'dark'
                                ? `linear-gradient(135deg, ${alpha(accentColor, 0.25)}, ${alpha(accentColor, 0.1)})`
                                : `linear-gradient(135deg, ${accentColor}15, ${accentColor}05)`,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            zIndex: 0
                        }}
                    >
                        <FormatQuote
                            sx={{
                                color: theme.palette.mode === 'dark' ? theme.palette.primary.light : accentColor,
                                fontSize: 24,
                                transform: 'rotate(180deg)'
                            }}
                        />
                    </Box>

                    <CardContent sx={{ p: 3, position: 'relative', zIndex: 1 }}>
                        <Stack spacing={2}>
                            {/* Rating */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Rating
                                    value={testimonial.rating}
                                    readOnly
                                    size="small"
                                    sx={{
                                        '& .MuiRating-iconFilled': {
                                            color: '#FFD700'
                                        }
                                    }}
                                />
                                {testimonial.verified && (
                                    <Verified
                                        sx={{
                                            color: accentColor,
                                            fontSize: 16,
                                            ml: 1
                                        }}
                                    />
                                )}
                            </Box>

                            {/* Comment */}
                            <Typography
                                variant="body1"
                                sx={{
                                    color: theme.palette.text.primary,
                                    lineHeight: 1.7,
                                    fontSize: '0.95rem',
                                    fontStyle: 'italic',
                                    minHeight: '80px',
                                    display: '-webkit-box',
                                    WebkitLineClamp: 4,
                                    WebkitBoxOrient: 'vertical',
                                    overflow: 'hidden'
                                }}
                            >
                                "{testimonial.comment}"
                            </Typography>

                            {/* Trip destination */}
                            {testimonial.tripDestination && (
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? alpha(accentColor, 0.2)
                                            : alpha(accentColor, 0.1),
                                        color: theme.palette.mode === 'dark' ? theme.palette.primary.light : accentColor,
                                        px: 2,
                                        py: 0.5,
                                        borderRadius: 2,
                                        fontSize: '0.8rem',
                                        fontWeight: 600,
                                        width: 'fit-content'
                                    }}
                                >
                                    📍 {testimonial.tripDestination}
                                </Box>
                            )}

                            {/* Client info */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2 }}>
                                <Avatar
                                    src={testimonial.avatar || undefined}
                                    sx={{
                                        width: 40,
                                        height: 40,
                                        backgroundColor: theme.palette.mode === 'dark'
                                            ? alpha(accentColor, 0.2)
                                            : alpha(accentColor, 0.1),
                                        color: theme.palette.mode === 'dark' ? theme.palette.primary.light : accentColor
                                    }}
                                >
                                    {testimonial.avatar ? null : <AccountCircle />}
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="subtitle2"
                                        sx={{
                                            fontWeight: 600,
                                            color: theme.palette.text.primary,
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        {testimonial.name}
                                    </Typography>
                                    <Typography
                                        variant="caption"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            fontSize: '0.8rem'
                                        }}
                                    >
                                        {testimonial.location}
                                    </Typography>
                                </Box>
                            </Box>
                        </Stack>
                    </CardContent>
                </Card>
            </motion.div>
        );
    };

    return (
        <Box
            id={spec.id}
            component="section"
            sx={{
                py: { xs: 6, md: 10 },
                backgroundColor,
                position: 'relative',
                overflow: 'hidden'
            }}
        >
            {/* Background decoration */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    opacity: 0.03,
                    backgroundImage: `radial-gradient(circle at 20% 80%, ${accentColor} 0%, transparent 70%),
                                     radial-gradient(circle at 80% 20%, ${accentColor} 0%, transparent 70%)`,
                    zIndex: 0
                }}
            />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                {/* Header */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, ease: "easeOut" as const }}
                >
                    <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 800,
                                mb: 2,
                                color: titleColor,
                                fontSize: { xs: '2rem', md: '2.5rem', lg: '3rem' },
                                background: `linear-gradient(45deg, ${titleColor}, ${accentColor})`,
                                backgroundClip: 'text',
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                lineHeight: 1.2
                            }}
                        >
                            {title}
                        </Typography>
                        <Typography
                            variant="h6"
                            sx={{
                                color: subtitleColor,
                                maxWidth: 600,
                                mx: 'auto',
                                lineHeight: 1.6,
                                fontSize: { xs: '1rem', md: '1.1rem' },
                                fontWeight: 400
                            }}
                        >
                            {subtitle}
                        </Typography>
                    </Box>
                </motion.div>

                {/* Testimonials Grid */}
                <motion.div
                    variants={containerVariants}
                    whileInView="visible"
                    viewport={{ once: true, margin: "-100px" }}
                >
                    <Grid container spacing={{ xs: 3, md: 4 }}>
                        {testimonials.slice(0, 6).map((testimonial: Testimonial, index: number) => (
                            <Grid item xs={12} md={6} lg={4} key={testimonial.id}>
                                <TestimonialCard
                                    testimonial={testimonial}
                                    isActive={index === currentTestimonial}
                                    onClick={() => setCurrentTestimonial(index)}
                                />
                            </Grid>
                        ))}
                    </Grid>
                </motion.div>

                {/* Navigation dots */}
                {testimonials.length > 1 && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5 }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 1,
                                mt: 4
                            }}
                        >
                            {testimonials.map((_: Testimonial, index: number) => (
                                <IconButton
                                    key={index}
                                    size="small"
                                    onClick={() => setCurrentTestimonial(index)}
                                    sx={{
                                        width: 12,
                                        height: 12,
                                        p: 0,
                                        backgroundColor: index === currentTestimonial
                                            ? (theme.palette.mode === 'dark' ? "#fff" : accentColor)
                                            : alpha(accentColor, 0.3),
                                        borderRadius: '50%',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            backgroundColor: theme.palette.mode === 'dark' ? theme.palette.primary.light : accentColor,
                                            transform: 'scale(1.2)'
                                        }
                                    }}
                                />
                            ))}
                        </Box>
                    </motion.div>
                )}

                {/* Overall rating summary */}
                {actionButtonText && secondaryButtonText && <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                >
                    <Box
                        sx={{
                            mt: { xs: 6, md: 8 },
                            p: 4,
                            borderRadius: 3,
                            background: theme.palette.mode === 'dark'
                                ? alpha(accentColor, 0.1)
                                : alpha(accentColor, 0.05),
                            border: theme.palette.mode === 'dark'
                                ? `1px solid ${alpha(accentColor, 0.2)}`
                                : `1px solid ${alpha(accentColor, 0.1)}`,
                            textAlign: 'center'
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={4}
                            justifyContent="center"
                            alignItems="center"
                            sx={{
                                gap: 2,
                            }}
                        >
                            {actionButtonText && <Box>
                                <ApplicationButton
                                    variant={actionButtonVariant}
                                    sx={{
                                        color: actionButtonTextColor,
                                        backgroundColor: actionButtonBackground
                                    }}
                                    href={actionButtonHref}
                                >
                                    {actionButtonText}
                                </ApplicationButton>
                            </Box>}
                            {secondaryButtonText && <Box>
                                <ApplicationButton
                                    variant={secondaryButtonVariant}
                                    sx={{
                                        color: secondaryButtonTextColor,
                                        backgroundColor: secondaryButtonBackground
                                    }}
                                    href={secondaryButtonHref}
                                >
                                    {secondaryButtonText}
                                </ApplicationButton>
                            </Box>}
                        </Stack>
                    </Box>
                </motion.div>}
            </Container>
        </Box>
    );
};

export default ClientTestimonials;
